import React, { useState } from 'react';

import { Button, Input, Modal, showToast } from '@attentive-platform/stem-ui';

import { ModalActions, ModalBody, ModalSubtitle, ModalTitle } from '../../styled';
import { apiErrorMessage } from '../../Utils/apiErrors';
import { createPlanroom, updatePlanroom } from './api';

const CredentialModal = ({ open, company, planroom, onClose, onSaved }) => {
  const isEdit = Boolean(planroom?.id);
  const [name, setName] = useState(planroom?.name || '');
  const [url, setUrl] = useState(planroom?.url || '');
  const [username, setUsername] = useState(planroom?.username || '');
  const [password, setPassword] = useState(planroom?.password || '');
  const [submitting, setSubmitting] = useState(false);

  const trimmedName = name.trim();
  const trimmedUrl = url.trim();
  const trimmedUsername = username.trim();

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleNameChange = event => setName(event.target.value);
  const handleUrlChange = event => setUrl(event.target.value);
  const handleUsernameChange = event => setUsername(event.target.value);
  const handlePasswordChange = event => setPassword(event.target.value);

  const handleSubmit = async () => {
    if (submitting) return;
    if (!isEdit && !company?.id) return;
    if (!trimmedName) {
      showToast('Please enter a planroom name', 'error');
      return;
    }
    if (!trimmedUrl) {
      showToast('Please enter a login URL', 'error');
      return;
    }
    if (!trimmedUsername) {
      showToast('Please enter a username', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const saved = isEdit
        ? await updatePlanroom({
            planroomId: planroom.id,
            name: trimmedName,
            url: trimmedUrl,
            username: trimmedUsername,
            password
          })
        : await createPlanroom({
            companyId: company.id,
            name: trimmedName,
            url: trimmedUrl,
            username: trimmedUsername,
            password
          });
      if (saved.error) {
        showToast(
          apiErrorMessage(saved.error, isEdit ? 'Could not update credential' : 'Could not add credential'),
          'error'
        );
        return;
      }
      showToast(isEdit ? 'Credential updated' : 'Credential added', 'success');
      onSaved(saved.data);
    } catch {
      showToast(isEdit ? 'Could not update credential' : 'Could not add credential', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={open}
      onClose={handleClose}
      heading={<ModalTitle>{isEdit ? 'Edit credential' : 'Add credential'}</ModalTitle>}
      content={
        <ModalBody>
          <ModalSubtitle>{company?.name}</ModalSubtitle>
          <div>
            <label className='mb-2 block text-sm font-medium text-gray-900'>PlanRoom</label>
            <Input value={name} className='w-full' placeholder='e.g. BuildingConnected' onChange={handleNameChange} />
          </div>
          <div>
            <label className='mb-2 block text-sm font-medium text-gray-900'>Login URL</label>
            <Input value={url} className='w-full' placeholder='https://' onChange={handleUrlChange} />
          </div>
          <div>
            <label className='mb-2 block text-sm font-medium text-gray-900'>Username</label>
            <Input value={username} className='w-full' onChange={handleUsernameChange} />
          </div>
          <div>
            <label className='mb-2 block text-sm font-medium text-gray-900'>Password</label>
            <Input type='password' value={password} className='w-full' onChange={handlePasswordChange} />
          </div>
          <ModalActions>
            <Button size='medium' variant='text' color='primary' disabled={submitting} onClick={handleClose}>
              Cancel
            </Button>
            <Button color='primary' size='medium' loading={submitting} onClick={handleSubmit}>
              Save
            </Button>
          </ModalActions>
        </ModalBody>
      }
    />
  );
};

export default CredentialModal;
